import React, { useEffect, useState } from "react";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "../../store";
import { Customer, CustomerFormData } from "./customer";
import {
  selectBranches,
  selectBranchLoading,
} from "../branch/branchSlice";
import BaseModal from "../../components/common/BaseModal";
import FormInput from "../../components/common/FormInput";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from "@mui/material/InputAdornment";
import EmailField from "../../form/EmailField";
import PhoneField from "../../form/PhoneField";
import ZipCodeField from "../../form/ZipCodeField";
import TextInputField from "../../form/TextInputField";
import { useDispatch } from "react-redux";
import { createCustomer, editCustomer } from "./customerSlice";
import CitySelect from "../../form/CityField";
import StateSelect from "../../form/StateField";
import CommonAddress from "../../form/CommonAddress";
import { cityToStateOptions } from "../../constants/locationOptions"


interface CreateCustomerModalProps {
  isOpen: boolean;
  onRequestClose: (hasData: boolean) => void;
  onSuccess?: () => void;
  customer:Customer | null;

}

type CustomerCategory = {
  id: number;
  label: string;
};

const CUSTOMER_TYPES: CustomerCategory[] = [
  { id: 1, label: "Regular" },
  { id: 2, label: "Premium" },
  { id: 3, label: "VIP" },
];

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  isOpen,
  onRequestClose,
  onSuccess,
  customer,

}) => {

  useEffect(() => {
    console.log("CreateCustomerModal mounted/updated");
    console.log("isOpen:", isOpen);
    console.log("customer:", customer);
  }, [isOpen, customer]);

  const branches = useAppSelector(selectBranches);
  const dispatch = useDispatch();

  const isEditMode = !!customer;
  const branchesLoading = useAppSelector(selectBranchLoading);
  const customerLoading = useAppSelector((state: any) => state.customers?.loading || false);
  const [sameAsAbove, setSameAsAbove] = useState(false);
  const [sameAsAboveWhatsapp, setSameAsAboveWhatsapp] = useState(false);
  const [formData, setFormData] = useState<CustomerFormData>({
    name: "",
    code: "",
    email: "",
    phone: "",
    whatsapp: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    status: true,
    type: "regular", // Default type
    branchId: "",
    referenceBy: "",
  });

  useEffect(()=>{
    console.log("Customer data in modal:", customer);
    // if(isOpen){
      if (customer) {
        setFormData({
          name: customer.name || "",
          code: customer.code || "",
          email: customer.email || "",
          phone: customer.phone || "",
          whatsapp: customer.whatsapp || "",
          address: customer.address || "",
          city: customer.city || "",
          state: customer.state || "",
          zipCode: customer.zipCode || "",
          contactPerson: customer.contactPerson || "",
          contactPhone: customer.contactPhone || "",
          contactEmail: customer.contactEmail || "",
          status: customer.status,
          type: customer.type || "regular",
          branchId: customer.branchId || "",
          referenceBy: customer.referenceBy || "",
        });
      } else {
        setFormData({
          name: "",
          code: "",
          email: "",
          phone: "",
          whatsapp: "",
          address: "",
          city: "",
          state: "",
          zipCode: "",
          contactPerson: "",
          contactPhone: "",
          contactEmail: "",
          status: true,
          type: "regular",
          branchId: "",
          referenceBy: "",
        });
      }
    // }
    
  }, [isOpen,customer]);
   
   useEffect(() => {
    if (sameAsAbove) {
      setFormData(prev => ({
        ...prev,
        contactPerson: prev.name || "",
        contactPhone: prev.phone || "",
      }));
    }
    else if (sameAsAboveWhatsapp){
      setFormData(prev => ({
        ...prev,
        whatsapp: prev.phone || "",
      }));
    }
  }, [sameAsAbove, formData.name, formData.phone, setFormData]);



  useEffect(() => {
    if (sameAsAboveWhatsapp){
      setFormData(prev => ({
        ...prev,
        whatsapp: prev.phone || "",
      }));
    }
  },[sameAsAboveWhatsapp,formData.whatsapp, formData.phone])

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSameAsAbove(e.target.checked);
  };

  const handleCheckboxChangeWhatsapp = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSameAsAboveWhatsapp(e.target.checked);
  };

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [touchedWhileTyping, setTouchedWhileTyping] = useState({
    email: false,
    phone: false,
    whatsapp:false,
    zipCode: false,
    contactEmail: false,
    contactPhone: false,
  });

  // Removed the old logic for auto-setting state from cityToStateOptions
  // Now handled by onStateChange in CitySelect

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (!touchedWhileTyping[name as keyof typeof touchedWhileTyping]) {
      setTouchedWhileTyping((prev) => ({
        ...prev,
        [name]: true,
      }));
    }

    setFormData((prev) => {
      // Clear city if state is cleared
      if (name === "state" && (!value || value === "")) {
        return {
          ...prev,
          state: value,
          city: "",
        };
      }
      return {
        ...prev,
        [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
      };
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  
  const validateForm = (): boolean => {
    const {
      name, phone, email, contactEmail
    } = formData;
  
    // Backend only requires name and phone, email is optional
    const emailValid = email.trim() === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const contactEmailValid = contactEmail.trim() === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail);
  
    return (
      name.trim() !== "" &&
      phone.trim() !== "" &&
      emailValid &&
      contactEmailValid 
    );
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Form validation status:", validateForm());
    console.log("Form data:", formData);

    if (!validateForm()) {
      console.error("Form validation failed");
      return;
    }

    // Map frontend form data to backend expected format
    const customerData = {
      ...formData,
      customerType: formData.type, // Now using the correct values (regular, premium, vip)
      status: formData.status // Keep as boolean for frontend
    };

    console.log("Submitting Form Data:", JSON.stringify(customerData));

    try {
      if (isEditMode) {
        console.log("Dispatching editCustomer action");
        const result = await dispatch(editCustomer({ id: customer.id, customerData }) as any);
        console.log("Edit customer result:", result);
        
        if (result.type === 'customers/editCustomer/rejected') {
          console.error("Customer update failed:", result.payload);
          return; // Don't close modal on error
        }
      } else {
        console.log("Dispatching createCustomer action");
        const result = await dispatch(createCustomer(customerData) as any);
        console.log("Create customer result:", result);

        if (result.type === 'customers/createCustomer/rejected') {
          console.error("Customer creation failed:", result.payload);
          return; // Don't close modal on error
        }
      }
      console.log("Customer operation successful, calling onSuccess");
      if (onSuccess) {
        onSuccess();
      }
      return; // Close via parent state change, don't trigger onClose
    } catch (error: any) {
      console.error("Error submitting form:", error);
      // Don't close the modal or clear the form on error
      return;
    }
  }
  const hasFormData = (): boolean => {
    return !!(
      formData.name.trim() ||
      formData.code.trim() ||
      formData.email.trim() ||
      formData.phone.trim() ||
      formData.whatsapp.trim() ||
      formData.address.trim() ||
      formData.city.trim() ||
      formData.state.trim() ||
      formData.zipCode.trim() ||
      formData.contactPerson.trim() ||
      formData.contactPhone.trim() ||
      formData.contactEmail.trim() ||
      formData.branchId.trim() ||
      formData.referenceBy.trim()
    );
  };

  const isFormValid = validateForm();
  console.log("CreateCustomerModal render - isOpen:", isOpen);
  
  return (
  
    // <BaseModal isOpen={isOpen} onClose={onClose} title={ isEditMode ? "Edit Customer" : "Create Customer"}>
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={() => onRequestClose(hasFormData())}
      ></div>
      {/* Slide-in Panel */}
      <div
        className={`absolute right-0 top-0 h-full w-[30%] bg-white shadow-xl transform transition-transform duration-1000 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">

          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? "Edit Customer" : "Create Customer"}
            </h2>
            <button
              onClick={() => onRequestClose(hasFormData())}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Name and Code */}
              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}

                <TextInputField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
                />
                {/* <TextInputField
                  label="Name Plate"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
                /> */}
              {/* </div> */}

              {/* Email and Phone */}
              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
                {/* <EmailField
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  // showError={emailTouched}
                  showError={touchedWhileTyping.email}
                /> */}
                <PhoneField
                  label="Phone"
                  name="phone"
                  value={formData.phone || ""}
                  onChange={handleChange}
                  required
                  showError={touchedWhileTyping.phone}
                />

                <div className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    id="sameAsAboveWhatsapp"
                    checked={sameAsAboveWhatsapp}
                    onChange={handleCheckboxChangeWhatsapp}
                    className="h-4 w-4"
                  />
                  <label
                    htmlFor="sameAsAbove"
                    className="text-gray-600 text-sm cursor-pointer"
                  >
                    Same as above
                  </label>
                </div>

                <PhoneField
                  label="WhatsApp"
                  name="whatsapp"
                  value={formData.whatsapp || ""}
                  onChange={handleChange}
                  required
                  showError={touchedWhileTyping.whatsapp}
                />

              {/* </div> */}

              {/* <TextInputField
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">*/}
                <CitySelect
                  state={formData.state}
                  value={formData.city}
                  onChange={handleChange}
                  onStateChange={(stateName, stateId) => {
                    setFormData(prev => ({
                      ...prev,
                      state: stateName,
                    }));
                  }}
                  required

                />

                <StateSelect
                  value={formData.state}
                  onChange={handleChange}
                  required
                 
                />

                

                <ZipCodeField
                  label="ZIP Code"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  showError={touchedWhileTyping.zipCode}
                />
              {/* </div>  */}

                {/* <CommonAddress
                  formData={formData} 
                  onChange={handleChange} 
                  touchedWhileTyping={touchedWhileTyping}
                /> */}
              <div className="flex gap-2"> 
                <p>Contact Person</p>
                {/* Contact Person Checkbox */}
                <div className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    id="sameAsAbove"
                    checked={sameAsAbove}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4"
                  />
                  <label
                    htmlFor="sameAsAbove"
                    className="text-gray-600 text-sm cursor-pointer"
                  >
                    Same as above
                  </label>
                </div>
              </div>
              {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4"> */}
                {/* <FormInput
                  icon={<UserIcon className="h-5 w-5" />}
                  label="Contact Person"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  placeholder="Enter contact person name"
                  required
                  error={errors.contactPerson}
                /> */}
                
                <TextInputField
                  label="Contact Person"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                  icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
                />
                {/* <FormInput
                  icon={<PhoneIcon className="h-5 w-5" />}
                  label="Contact Phone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  placeholder="Enter contact phone"
                  required
                  error={errors.contactPhone}
                /> */}
                <PhoneField
                  label="Contact Phone"
                  name="contactPhone"
                  value={formData.contactPhone || ""}
                  onChange={handleChange}
                  required
                  showError={touchedWhileTyping.contactPhone}
                />

                <EmailField
                  label="Contact Email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  showError={touchedWhileTyping.contactEmail}
                />

                <TextInputField
                  label="Reference By"
                  name="referenceBy"
                  value={formData.referenceBy || ""}
                  onChange={handleChange}
                  icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
                />
              {/* </div> */}

              {/* <FormInput
                icon={<EnvelopeIcon className="h-5 w-5" />}
                label="Contact Email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                placeholder="Enter contact email"
                required
                error={errors.contactEmail}
              /> */}
              
                
                {/* <TextInputField
                  label="Branch Id"
                  name="branchId"
                  value={formData.branchId}
                  onChange={handleChange}
                  required
                  icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
                /> */}
              

              {/* Submit Error Message */}
              {errors.submit && (
                <p className="text-sm text-red-500 dark:text-red-400 mt-2">
                  {errors.submit}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-between gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => onRequestClose(hasFormData())}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                {/* <button
                  type="submit"
                  className="inline-flex items-center mx-4 px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-secondary-800 transition-colors"          >
                  {isEditMode ? "Update Customer" : "Create Customer"}
                </button> */}
                
                {/* <button
                  type="submit"
                  className={`inline-flex items-center mx-4 px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white transition-colors ${
                    isEditMode
                      ? "bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600"
                      : "bg-gray-400 cursor-not-allowed"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-secondary-800`}
                  // disabled={isEditMode? validateForm() : !validateForm()}
                  disabled={!validateForm()}
                >
                  {isEditMode ? "Update Customer" : "Create Customer"}
                </button> */}

                <button
                  type="submit"
                  disabled={!validateForm() || customerLoading}
                  className={`inline-flex items-center px-4 py-2 shadow-sm text-sm font-medium rounded-md text-white transition-colors ${
                    validateForm() && !customerLoading
                      ? "bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {customerLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {isEditMode ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    isEditMode ? "Update Customer" : "Create Customer"
                  )}
                </button>

                {/* <button
                  type="submit"
                  className={`inline-flex items-center mx-4 px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white transition-colors ${
                    isEditMode
                      ? "bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600"
                      : "bg-gray-400 cursor-not-allowed"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-secondary-800`}
                  disabled={isEditMode? validateForm() : !validateForm()}
                >
                  {isEditMode ? "Update Customer" : "Create Customer"}
                </button> */}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    // </BaseModal>

  );
};

export default CreateCustomerModal;
